import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';

const APP_NAME = 'HubbubHR';

/**
 * Gives every page a unique document title (WCAG 2.4.2) and, on in-app navigation,
 * moves focus to the main content and announces the new page (WCAG 2.4.3).
 *
 * The title comes from the deepest route's `title`, falling back to a readable
 * version of its path. A `section` / `subSection` route param (e.g. the employee
 * profile's "TimeOff") is prefixed so each profile section has its own title.
 */
@Injectable()
export class AccessibleTitleStrategy extends TitleStrategy {
  private isFirstNavigation = true;

  constructor(
    private readonly title: Title,
    private readonly translate: TranslateService,
    private readonly liveAnnouncer: LiveAnnouncer
  ) {
    super();
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const pageTitle = this.getPageTitle(snapshot);
    this.title.setTitle(pageTitle ? `${pageTitle} | ${APP_NAME}` : APP_NAME);

    // On the first load the screen reader reads the page itself; after that the
    // browser doesn't reload, so move focus to the new content and announce it.
    if (this.isFirstNavigation) {
      this.isFirstNavigation = false;
      return;
    }

    setTimeout(() => {
      document.getElementById('main-content')?.focus();
      if (pageTitle) {
        this.liveAnnouncer.announce(pageTitle, 'polite');
      }
    });
  }

  private getPageTitle(snapshot: RouterStateSnapshot): string {
    let route: ActivatedRouteSnapshot = snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const base = this.buildTitle(snapshot) ?? this.titleFromPath(route.routeConfig?.path);
    const section = [route.paramMap.get('subSection'), route.paramMap.get('section')]
      .filter(Boolean)
      .map(part => this.humanize(part));

    return [...section, base]
      .filter(Boolean)
      .map(part => this.translate.instant(part, { Default: part }))
      .join(' – ');
  }

  // "TimeOff/BuySell/Approvals" -> "Time Off Buy Sell Approvals"; route params and editor ids are skipped
  private titleFromPath(path: string | undefined): string {
    return (path ?? '')
      .split('/')
      .filter(segment => segment && !segment.startsWith(':'))
      .map(segment => this.humanize(segment))
      .join(' ');
  }

  private humanize(value: string): string {
    return value
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]/g, ' ')
      .trim();
  }
}
